import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import styled, { css } from "styled-components"
import { useWindowScroll } from "react-use"
import { useIntl } from "gatsby-plugin-react-intl"

import { Container } from "./Grid"
import LanguageSwitcher from "./LanguageSwitcher"
import Link from "./Link"
import HeaderShortlinks from "./HeaderShortlinks"
import HeaderSearch from "./HeaderSearch"
import HeaderNavigationMobile from "./HeaderNavigationMobile"
import HeaderNavigationDesktop from "./HeaderNavigationDesktop"
import Signet from "../components/Signet"
import { ScreenReaderText } from "../components/Accessibility"

import { gridTheme, media, mixins } from "../styles"
import WordmarkSVG from "../images/logos/solaris-wordmark.inline.svg"

const transitionTime = "500ms"
export const headerHeight = 50
export const headerHeightLg = 80

const HeaderWrapper = styled.div`
  background: ${({ theme }) => theme.white};
  border-bottom: 1px solid transparent;
  position: fixed;
  top: 0;
  transition: border-color ${transitionTime};
  width: 100%;
  z-index: 10;

  @media ${media.lg} {
    border-color: ${({ theme }) => theme.greyLight};
  }

  ${(props) =>
    props.$scrolled &&
    css`
      && {
        border-color: transparent;
      }
    `}
`

const HeaderContainer = styled(Container).attrs({ as: "header" })`
  ${mixins.headerSpacing}
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  font-size: 13px;
  height: ${headerHeight}px;
  position: relative;

  @media ${media.md} {
    font-size: 15px;
    height: 60px;
  }

  @media ${media.lg} {
    font-size: 15px;
    height: ${headerHeightLg}px;
    padding-right: ${gridTheme.container.padding}px;
  }
`

export const Logo = styled.div`
  color: ${({ theme }) => theme.spaceblue};
  position: relative;
  z-index: 2;
  width: 62px;

  @media ${media.md} {
    width: 82px;
  }

  svg {
    display: block;
    height: auto;
    width: 100%;
  }
`

const HeadSignet = styled(Signet)`
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);
  bottom: 0;
  height: 50px;
  transition: ${transitionTime};
  width: 50px;

  ${(props) =>
    props.$scrolled &&
    css`
      && {
        transform: translate(-50%, 50%) scale(0.5714) rotate(-180deg);

        @media ${media.lg} {
          transform: translate(-50%, -25px) scale(0.5714) rotate(-180deg);
        }

        @media ${media.xl} {
          transform: translate(-50%, -20px) scale(0.5714) rotate(-180deg);
        }
      }
    `}

  @media ${media.md} {
    height: 50px;
    width: 50px;
  }

  @media ${media.lg} {
    bottom: -50px;
  }

  @media ${media.xl} {
    bottom: -55px;
    height: 70px;
    width: 70px;
  }

  @media print {
    display: none;
  }
`

const HeaderCenter = styled.div`
  align-items: center;
  display: flex;
  order: 1;

  @media ${media.lg} {
    justify-content: center;
    position: absolute;
    width: 100%;
    z-index: 0;
  }
`

const HeaderTopRight = styled.div`
  ${mixins.headerSpacing}
  align-items: center;
  display: flex;
  margin: 0 0 0 auto;
  position: relative;
  z-index: 2;
`

const Search = styled(HeaderSearch)`
  display: none !important;

  .search-label {
    font-weight: 700;
  }

  @media ${media.lg} {
    display: flex !important;
  }
`

const SkipToAnchor = styled.ul`
  left: -2px;
  list-style: none;
  margin: 0;
  padding: 0;
  position: fixed;
  top: ${headerHeight + 0}px;
  z-index: 1000;

  @media ${media.sm} {
    left: -20px;
    position: relative;
    top: 0;
  }

  li {
    margin: 0;
    padding: 0;
  }

  a {
    background-color: ${({ theme }) => theme.white};
    /* border-radius: 20px; */
    color: ${({ theme }) => theme.spaceblue};
    font-size: 13px;
    font-weight: 700;
    left: -1000px;
    outline-offset: 1px !important;
    padding: 8px;
    position: absolute;
    text-decoration: underline;
    top: -1000px;
    z-index: 1;

    @media ${media.md} {
      font-size: 15px;
      padding: 6px 8px;
    }

    &:focus {
      position: relative;
      top: 0;
      left: 0;
      margin: 0 20px;
    }
  }
`

const Header = ({
  logo,
  nav,
  signet,
  shortlinks,
  hideLangSwitch,
  hideSearch,
}) => {
  const { y } = useWindowScroll()
  const [scrollPosition, setScrollPosition] = useState(0)
  const intl = useIntl()

  useEffect(() => {
    setScrollPosition(y)
  }, [y])

  const data = useStaticQuery(graphql`
    query HeaderQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `)

  return (
    <HeaderWrapper $scrolled={scrollPosition > 40}>
      <HeaderContainer
        id="headerHeight"
        aria-label={intl.formatMessage({
          id: "accessibility.regions.header",
        })}
        data-testid="header"
      >
        {logo ? (
          logo
        ) : (
          <Link
            aria-label={intl.formatMessage({
              id: "accessibility.header.homeLink.general",
            })}
            title={intl.formatMessage({
              id: "accessibility.header.homeLink.general",
            })}
            to="/"
          >
            <Logo>
              <ScreenReaderText>
                {data.site.siteMetadata.title}
              </ScreenReaderText>
              <span aria-hidden="true">
                <WordmarkSVG />
              </span>
            </Logo>
          </Link>
        )}
        {/* NOTE:
            The megamenu *requires* a focusable element immediately before it with the attribute `data-close-menu`.
            This is to ensure that it closes when tabbing out. If you change something here, you MUST ensure that
            the new element:
            1. is focusable
            2. is the first focusable element when shift-tabbing backwards from the first element in the megamenu
            3. retains the `data-close-menu` attribute
        */}

        <SkipToAnchor>
          <li>
            <a
              href="#main-navigation"
              title={intl.formatMessage({
                id: "accessibility.regions.mainNavigationLink",
              })}
              data-close-menu
            >
              {intl.formatMessage({
                id: "accessibility.regions.mainNavigation",
              })}
            </a>
          </li>
          <li>
            <a
              href="#meta-navigation"
              title={intl.formatMessage({
                id: "accessibility.regions.metaNavigationLink",
              })}
              data-close-menu
            >
              {intl.formatMessage({
                id: "accessibility.regions.metaNavigation",
              })}
            </a>
          </li>
          <li>
            <a
              href="#main"
              title={intl.formatMessage({
                id: "accessibility.regions.contentLink",
              })}
              data-close-menu
            >
              {intl.formatMessage({ id: "accessibility.regions.content" })}
            </a>
          </li>
          <li>
            <a
              href="#page-footer"
              title={intl.formatMessage({
                id: "accessibility.regions.footerLink",
              })}
              data-close-menu
            >
              {intl.formatMessage({ id: "accessibility.regions.footer" })}
            </a>
          </li>
        </SkipToAnchor>
        {/* you can pass an empty space character to hide the signet */}
        <div aria-hidden="true">
          {signet ? signet : <HeadSignet $scrolled={scrollPosition > 20} />}
        </div>
        <HeaderCenter data-testid="header-center" id="main-navigation">
          {nav ? (
            nav
          ) : (
            <>
              <HeaderNavigationMobile />
              <HeaderNavigationDesktop />
            </>
          )}
        </HeaderCenter>
        <HeaderTopRight data-testid="header-topright" id="meta-navigation">
          {shortlinks ? shortlinks : <HeaderShortlinks />}
          {!hideSearch && <Search mode="overlay" />}
          {!hideLangSwitch && <LanguageSwitcher />}
        </HeaderTopRight>
      </HeaderContainer>
    </HeaderWrapper>
  )
}

Header.propTypes = {
  hideLangSwitch: PropTypes.bool,
  hideSearch: PropTypes.bool,
  logo: PropTypes.object,
  nav: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  signet: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  shortlinks: PropTypes.object,
}

export default Header
